/**
 * AP-ACCEPT-01C: Internal Operations Acceptance Queue
 * Route: /finance/acceptance-queue (protected, requires ap.acceptance.review)
 *
 * Operations staff review, filter, and dispatch delivery submissions.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Inbox, RefreshCw, ChevronRight, Clock, CheckCircle2, XCircle, AlertTriangle, Package, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  PENDING_REVIEW:       { label: "Pending Review", bg: "#FEF3C7", text: "#92400E", priority: 1 },
  UNDER_REVIEW:         { label: "Under Review",   bg: "#EFF6FF", text: "#1E40AF", priority: 2 },
  RESUBMITTED:          { label: "Resubmitted",    bg: "#F5F3FF", text: "#5B21B6", priority: 1 },
  CORRECTION_REQUESTED: { label: "Clarification",  bg: "#FFF7ED", text: "#9A3412", priority: 3 },
  ACCEPTED:             { label: "Accepted",        bg: "#F0FDF4", text: "#166534", priority: 4 },
  PARTIALLY_ACCEPTED:   { label: "Partially Accepted", bg: "#F0FDF4", text: "#166534", priority: 4 },
  REJECTED:             { label: "Rejected",        bg: "#FEF2F2", text: "#991B1B", priority: 5 },
};

function StatusBadge({ status }) {
  const cfg = STATUS_LABELS[status] || { label: status, bg: "#F5F5F5", text: "#666" };
  return (
    <span style={{ display: "inline-block", padding: "3px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 600, background: cfg.bg, color: cfg.text, letterSpacing: "0.3px" }}>{cfg.label}</span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function AcceptanceQueue() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [stats, setStats] = useState({ pending: 0, under_review: 0, decided: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 50 };
      if (statusFilter === "pending") params.status = "PENDING_REVIEW,RESUBMITTED";
      else if (statusFilter !== "all") params.status = statusFilter.toUpperCase();

      const res = await axios.get(`${API}/api/ap/acceptance/queue`, { params, withCredentials: true });
      setSubmissions(res.data.items || []);
      setTotal(res.data.total || 0);

      // Load stats
      const [pendRes, urRes] = await Promise.all([
        axios.get(`${API}/api/ap/acceptance/queue`, { params: { status: "PENDING_REVIEW", page_size: 1 }, withCredentials: true }),
        axios.get(`${API}/api/ap/acceptance/queue`, { params: { status: "UNDER_REVIEW", page_size: 1 }, withCredentials: true }),
      ]);
      setStats({
        pending: pendRes.data.total || 0,
        under_review: urRes.data.total || 0,
        decided: (res.data.total || 0) - (pendRes.data.total || 0) - (urRes.data.total || 0),
      });
    } catch (err) {
      if (err.response?.status === 403) toast.error("You do not have permission to access the Acceptance Queue");
      else toast.error("Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="acceptance-queue" style={{ minHeight: "100vh", background: "#FBF7F2", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "32px 24px" }}>
        {/* Header */}
        <div style={{ marginBottom: "28px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
            <Inbox size={20} style={{ color: "#C9A15F" }} />
            <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "clamp(22px, 4vw, 28px)", fontWeight: 600, color: "#1A1A1A", margin: 0 }}>Acceptance Queue</h2>
          </div>
          <p style={{ fontSize: "13px", color: "#6B5B4A", margin: 0 }}>Review and process supplier delivery evidence submissions</p>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "14px", marginBottom: "24px" }}>
          {[
            { label: "Awaiting Review", value: stats.pending, icon: <Clock size={16} style={{ color: "#D97706" }} />, accent: stats.pending > 0 ? "#D97706" : undefined, testId: "stat-pending" },
            { label: "Under Review", value: stats.under_review, icon: <Package size={16} style={{ color: "#3B82F6" }} />, testId: "stat-under-review" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testId} style={{ background: "#FFFFFF", border: "1px solid #EDE5D8", borderRadius: "10px", padding: "14px 18px", display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#FAF6F0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{s.icon}</div>
              <div>
                <p style={{ fontSize: "20px", fontWeight: 700, color: s.accent || "#1A1A1A", margin: "0 0 2px" }}>{s.value}</p>
                <p style={{ fontSize: "11px", color: "#9A8A78", margin: 0 }}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap", alignItems: "center" }}>
          {[
            { key: "pending",      label: "Awaiting Review" },
            { key: "UNDER_REVIEW", label: "Under Review" },
            { key: "all",          label: "All" },
            { key: "ACCEPTED",     label: "Accepted" },
            { key: "REJECTED",     label: "Rejected" },
          ].map((f) => (
            <button
              key={f.key}
              data-testid={`queue-filter-${f.key}`}
              onClick={() => setStatusFilter(f.key)}
              style={{ padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 500, cursor: "pointer", border: "1px solid", transition: "all 0.15s", background: statusFilter === f.key ? "#1A1A1A" : "transparent", color: statusFilter === f.key ? "#F5EFE6" : "#6B5B4A", borderColor: statusFilter === f.key ? "#1A1A1A" : "#D4C5B0" }}
            >
              {f.label}
            </button>
          ))}
          <button onClick={load} disabled={loading} style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "6px", padding: "6px 12px", background: "transparent", border: "1px solid #D4C5B0", borderRadius: "8px", fontSize: "12px", color: "#6B5B4A", cursor: "pointer" }}>
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Table */}
        <div style={{ background: "#FFFFFF", border: "1px solid #EDE5D8", borderRadius: "12px", overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: "48px", textAlign: "center" }}>
              <div style={{ width: "28px", height: "28px", border: "2px solid #EDE5D8", borderTopColor: "#C9A15F", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
              <p style={{ color: "#9A8A78", fontSize: "13px" }}>Loading queue…</p>
            </div>
          ) : submissions.length === 0 ? (
            <div data-testid="queue-empty" style={{ padding: "56px 24px", textAlign: "center" }}>
              <CheckCircle2 size={36} style={{ color: "#A5D6A7", margin: "0 auto 12px" }} />
              <p style={{ color: "#6B5B4A", fontSize: "14px", fontWeight: 500 }}>Queue is clear</p>
              <p style={{ color: "#9A8A78", fontSize: "13px" }}>No submissions match the current filter</p>
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1.2fr 1fr 1fr 100px 30px", padding: "10px 20px", background: "#FAF6F0", borderBottom: "1px solid #EDE5D8", fontSize: "10px", fontWeight: 600, color: "#9A8A78", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                <span>PO / Event</span><span>Supplier</span><span>Lines</span><span>Submitted</span><span>Status</span><span />
              </div>
              {submissions.map((s) => (
                <div
                  key={s.id}
                  data-testid={`queue-row-${s.id}`}
                  onClick={() => navigate(`/ap/acceptance/${s.id}`)}
                  role="button" tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && navigate(`/ap/acceptance/${s.id}`)}
                  style={{ display: "grid", gridTemplateColumns: "1.4fr 1.2fr 1fr 1fr 100px 30px", padding: "14px 20px", borderBottom: "1px solid #F5F0E8", cursor: "pointer", alignItems: "center", transition: "background 0.12s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#FAF6F0")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <div>
                    <p style={{ fontSize: "13px", fontWeight: 600, color: "#1A1A1A", margin: 0 }}>{s.po_number || s.po_id?.slice(0, 8)}</p>
                    <p style={{ fontSize: "11px", color: "#9A8A78", margin: "2px 0 0" }}>{s.event_name || "—"}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: "12px", color: "#4A3A2A", margin: 0 }}>{s.supplier_name || s.supplier_id?.slice(0, 12)}</p>
                    <p style={{ fontSize: "11px", color: "#9A8A78", margin: "1px 0 0" }}>{s.submitted_by_email?.split("@")[0]}</p>
                  </div>
                  <span style={{ fontSize: "12px", color: "#6B5B4A" }}>{s.line_submissions?.length || 0} line{(s.line_submissions?.length || 0) !== 1 ? "s" : ""}</span>
                  <span style={{ fontSize: "12px", color: "#9A8A78" }}>{fmtDate(s.submitted_at)}</span>
                  <StatusBadge status={s.status} />
                  <ChevronRight size={14} style={{ color: "#C9A15F", justifySelf: "end" }} />
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
